import classNames from "classnames/bind";
import UIIcon from "common/blocks/UIIcon";
import useStructuredDataContext from "common/providers/structuredData/useStructuredDataContext";
import useHasMounted from "hooks/useHasMounted/useHasMounted";
import React, { useEffect, useRef, useState } from "react";
import type { BaseReactPlayerProps } from "react-player/base";
import { getTumbnailsFromVideoUrl } from "./api";
import Duration from "./Duration";
import styles from "./MediaPlayer.module.scss";
import useCookieConsentContext from "common/providers/cookieConsent/useCookieConsentContext";
import ReactPlayer, { ReactPlayerProps } from "react-player";

const cx = classNames.bind(styles);

export interface MediaPlayerProps extends ReactPlayerProps {
    backgroundMode?: boolean;
    uploadDate?: string;
    showPlayButton?: string;
    isBorderRounded?: boolean;
}

const MediaPlayer = (props: MediaPlayerProps) => {
    const hasMounted = useHasMounted();
    const {
        consent: { analytics, advertisement },
    } = useCookieConsentContext();

    const youtubeCookiesConsented = !!analytics && !!advertisement;
    const { createVideoStructuredData } = useStructuredDataContext();
    const [playing, setPlaying] = useState(props.backgroundMode || props.playing);
    const [played, setPlayed] = useState(0);
    const [seeking, setSeeking] = useState<boolean | null>();
    const [duration, setDuration] = useState(0);
    const [light, setLight] = useState<boolean>(false);
    const [volume, setVolume] = useState<number>(props.volume || 0.8);
    const [muted, setMuted] = useState(props.backgroundMode || props.muted);
    const [thumbnail, setThumbnail] = useState<string>("");

    useEffect(() => {
        const url = ((props.url || "") as string).replace("youtube-nocookie", "youtube");
        getTumbnailsFromVideoUrl(url as string).then((url) => {
            setThumbnail(url);
        });
    }, [props.url]);

    const playerRef = useRef<any | null>();

    useEffect(() => {
        if (
            !hasMounted ||
            !props.url ||
            typeof props.url !== "string" ||
            !createVideoStructuredData
        )
            return;
        createVideoStructuredData({
            contentUrl: props.url,
            description: props.description,
            name: props.title,
            uploadDate: props.uploadDate ?? new Date().toISOString(),
            thumbnailUrl: props.url,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMounted]);

    if (!hasMounted) {
        return null;
    }

    const handlePlay = () => {
        setLight(false);
        setPlaying(true);
    };

    const handlePause = () => setPlaying(false);

    const handlePlayPauseClick = () => {
        setPlaying((playing) => (playing = !playing));
    };

    const handleSeekKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const isArrowKey = ["ArrowLeft", "ArrowRight"].includes(e.key);
        const isEnterOrSpaceKey = [" ", "Enter"].includes(e.key);

        if (isArrowKey) {
            const value = parseFloat(e.currentTarget.value);
            playerRef.current?.seekTo(value);
            setPlayed(value);
            return;
        }

        if (isEnterOrSpaceKey) {
            setPlaying((playing) => (playing = !playing));
        }
    };

    const handleSeekMouseDown = () => setSeeking(true);

    const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPlayed(parseFloat(e.currentTarget.value));
    };

    const handleSeekMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
        playerRef.current?.seekTo(parseFloat(e.currentTarget.value));
    };

    const handleDuration: BaseReactPlayerProps["onDuration"] = (duration) => {
        setDuration(duration);
    };

    const handleProgress: BaseReactPlayerProps["onProgress"] = (progress) => {
        setPlayed(progress.played);
    };

    const handlePlayerClick = () => {
        setPlaying((playing) => (playing = !playing));
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMuted(false);
        setVolume(parseFloat(e.target.value));
    };

    const getSpeakerIcon = () => {
        if (muted || volume === 0) return "VolumeMute";
        if (volume < 0.4) return "VolumeLow";
        if (volume < 0.8) return "VolumeMid";
        return "VolumeHigh";
    };

    const handleToggleMuted = () => {
        setMuted((muted) => (muted = !muted));
    };

    const backgroundModeProps = {
        muted: true,
        light: false,
        loop: true,
        controls: false,
        onClick: () => null,
    };

    const { uploadDate, showPlayButton, ...rest } = props;
    const propsWithoutBackgroundMode = { ...rest };
    delete propsWithoutBackgroundMode.backgroundMode;

    const mergedProps: MediaPlayerProps = Object.assign(
        {
            className: cx("reactPlayer", props.isBorderRounded),
            config: {
                youtube: {
                    playerVars: {
                        modestbranding: 1,
                        start: Math.floor(played),
                    },
                    embedOptions: {
                        host: youtubeCookiesConsented
                            ? "https://www.youtube.com"
                            : "https://www.youtube-nocookie.com",
                    },
                },
            },
            height: "100%",
            light,
            muted,
            onClick: handlePlayerClick,
            onDuration: handleDuration,
            onPause: handlePause,
            onPlay: handlePlay,
            onProgress: handleProgress,
            played,
            playIcon: props.playIcon || (
                <button type="button" className={cx("playButton")}>
                    <UIIcon icon="Play" />
                    <span className={cx("playButtonText")}>Play Video</span>
                </button>
            ),
            playing,
            ref: playerRef,
            seeking,
            volume,
            width: "100%",
            pip: false,
            playbackRate: 1.0,
            ...propsWithoutBackgroundMode,
        },
        props.backgroundMode ? backgroundModeProps : {}
    );

    return (
        <figure
            className={cx("mediaPlayer", {
                isPlaying: playing,
                backgroundMode: props.backgroundMode,
                isBorderRounded: props.isBorderRounded,
            })}
        >
            {!mergedProps.light && !playing && played === 0 && (
                <button onClick={handlePlay} type="button" className={cx("playButton")}>
                    <UIIcon icon="Play" />
                    <span className={cx("playButtonText")}>Play Video</span>
                </button>
            )}
            <ReactPlayer
                key={youtubeCookiesConsented ? "player-consented" : "player-unconsented"}
                {...mergedProps}
                light={thumbnail}
            />
            {!mergedProps.light && (
                <div className={cx("baseControls")} role="group" aria-label="Video controls">
                    <button
                        className={cx("playPauseButton", {
                            hide: !playing && !played,
                        })}
                        onClick={handlePlayPauseClick}
                        aria-label={playing ? "Pause" : "Play"}
                    >
                        {playing ? <UIIcon icon="Pause" /> : <UIIcon icon="Play" />}
                    </button>
                    <input
                        type="range"
                        min={0}
                        max={0.999999}
                        step="any"
                        onMouseDown={handleSeekMouseDown}
                        onChange={handleSeekChange}
                        onMouseUp={handleSeekMouseUp}
                        onKeyUp={handleSeekKeyUp}
                        className={cx("seek", "range", {
                            hide: !playing && !played,
                        })}
                        aria-label="Playback position"
                        value={played}
                    />
                    <span
                        className={cx("volume", {
                            hide: !playing && !played,
                        })}
                    >
                        <label className={cx("speaker")}>
                            <input
                                className={cx("speakerCheckbox")}
                                disabled={volume === 0}
                                type="checkbox"
                                onClick={handleToggleMuted}
                                defaultChecked={muted}
                                aria-label={muted || volume === 0 ? "Unmute" : "Mute"}
                            />
                            <UIIcon icon={getSpeakerIcon()} />
                        </label>
                        <input
                            type="range"
                            min={0}
                            max={0.999999}
                            step="any"
                            onChange={handleVolumeChange}
                            className={cx("volume", "range")}
                            value={muted ? 0 : volume}
                            aria-label="Volume"
                        />
                    </span>
                    <span
                        className={cx("timeStamps", {
                            hide: !playing && !played,
                        })}
                        role="group"
                    >
                        <Duration seconds={duration * played} ariaLabel="Playback timestamp" />
                        {" / "}
                        <Duration seconds={duration} ariaLabel="Total duration" />
                    </span>
                </div>
            )}
        </figure>
    );
};

export default MediaPlayer;
